// Static data for Emerging Risk Lab Pathway (insurance mode).
// All copy is deterministic and pre-written. No live LLM, no real
// underwriting data, no regulated advice.

export const PRODUCT = {
  name: "Emerging Risk Lab Pathway",
  altName: "Insurance Lab Pathway",
  coreLine:
    "A governed AI playground for new risks, new technologies and specialty insurance adoption.",
  heroTitle: "From emerging risk to governed insurance pathway.",
  heroSubtitle:
    "Emerging Risk Lab Pathway helps specialty insurers, universities, brokers, technology firms and sponsors explore how new risks become understandable, insurable and deployable — using synthetic scenarios, bounded AI workflows, evidence boundaries and public-safe receipts.",
  sponsorPositioningLine:
    "Sponsor-backed · university-hosted · KATLAS-governed · market-facing",
  sponsorThoughtLine:
    "Sponsor the market conversation on how new risks become governable, insurable and deployable.",
};

// Top-of-page positioning paragraph — appears persistently above the hero.
export const POSITIONING = {
  line1:
    "KATLAS governs AI-assisted operational decisions across organisational boundaries.",
  line2:
    "It does not replace underwriting, claims, ERP or workflow systems. It provides the evidence, authority and operational receipts that enable those systems to act with confidence.",
};

// Operational Mission — programme framing, not product framing.
export const OPERATIONAL_MISSION = {
  objective:
    "Reduce friction across AI-assisted insurance workflows while preserving accountable decision-making.",
  currentPilot: "Governed SME Cyber Placement",
  outcomes: [
    "Reduced referral handling time",
    "Fewer duplicated compliance checks",
    "Improved decision provenance",
    "Faster exception resolution",
  ],
  currentStage: "Operational Efficiency Pilot",
};

// Platform boundaries — what KATLAS replaces (nothing) and where it sits.
export const PLATFORM_BOUNDARIES = {
  flow: [
    { slug: "ai", label: "AI", role: "Makes recommendations" },
    { slug: "katlas", label: "KATLAS", role: "Governs", katlas: true },
    {
      slug: "insurance-platform",
      label: "Insurance Platform",
      role: "Executes (Guidewire / DXC / equivalent)",
    },
    { slug: "erp", label: "ERP", role: "Records (financial / contractual)" },
    { slug: "claims", label: "Claims", role: "Continues" },
  ],
  reassurance:
    "KATLAS does not replace your insurance platform, your ERP or your claims system. It is the governance layer that lets them act with confidence on AI-assisted decisions.",
};

// Operational Debt — single editorial visual with two states.
export const OPERATIONAL_DEBT = {
  states: [
    {
      slug: "with-governance",
      label: "With KATLAS",
      caption: "Volume scales without operational debt.",
      bars: [
        { slug: "volume", label: "Volume", value: 60 },
        { slug: "governance", label: "Governance", value: 96, tone: "ok" },
        { slug: "debt", label: "Operational Debt", value: 8, tone: "ok" },
      ],
    },
    {
      slug: "without-governance",
      label: "Without runtime governance",
      caption:
        "As AI volume increases, governance erodes and operational debt compounds.",
      bars: [
        { slug: "volume", label: "Volume", value: 96 },
        { slug: "governance", label: "Governance", value: 24, tone: "warn" },
        { slug: "debt", label: "Operational Debt", value: 94, tone: "warn" },
      ],
    },
  ],
};

// Operational confidence — not AI confidence. Belongs alongside the scenario.
export const OPERATIONAL_CONFIDENCE = [
  { slug: "authority", label: "Authority", value: "98%", tone: "ok" },
  { slug: "evidence", label: "Evidence", value: "94%", tone: "ok" },
  { slug: "policy", label: "Policy Alignment", value: "100%", tone: "ok" },
  { slug: "decision", label: "Decision Confidence", value: "96%", tone: "ok" },
  {
    slug: "human-review",
    label: "Human Review",
    value: "Required",
    tone: "warn",
  },
];

export const DISCLAIMER_LINES = [
  "This is a synthetic insurance innovation playground.",
  "It does not provide regulated insurance advice.",
  "It does not bind cover.",
  "It does not price risk.",
  "It does not settle claims.",
  "It does not make underwriting decisions.",
  "It does not imply Lloyd's, Mosaic or any insurer endorsement unless formally approved.",
];

export const STAKEHOLDER_DISCLAIMER =
  "Named organisations are used as illustrative pathway stakeholders only and do not imply endorsement, participation or approval.";

export const AI_NOTICE =
  "Example advisory text only. No live LLM. No real insurance data. No underwriting, claims, pricing or regulated advice.";

export const KATLAS_DEFINITION =
  "Runtime Operational Governance for AI-enabled multi-party workflows.";

// Architecture hierarchy — KATLAS as the platform; Insurance is one journey
// module on top of one reusable runtime.
export const HIERARCHY = [
  {
    label: "KATLAS",
    descriptor: "Runtime Operational Governance Platform",
  },
  {
    label: "Governance Engine",
    descriptor: "Receipts · Authority · Evidence · Policy · Exceptions",
  },
  {
    label: "Operational Governance Centre",
    descriptor: "Live runtime view — decisions, authority, exceptions, receipts",
  },
  {
    label: "Journey Modules",
    descriptor:
      "Insurance (current) · Freight · Health · Cities · Manufacturing",
  },
  {
    label: "Cierge Labs",
    descriptor: "Discovery · Pilot Design · Deployment",
  },
  {
    label: "Emerging Risk Lab Pathway (Insurance)",
    descriptor: "First journey module — this showcase",
    current: true,
  },
];

// The six governance facets of the KATLAS runtime node. Used everywhere.
export const GOVERNANCE_FACETS = [
  {
    slug: "authority",
    label: "Authority",
    blurb: "Who is permitted to act, and on what.",
  },
  {
    slug: "evidence",
    label: "Evidence",
    blurb: "What is relied upon, what is withheld.",
  },
  {
    slug: "policy",
    label: "Policy",
    blurb: "Which version of which rule governs this action.",
  },
  {
    slug: "receipts",
    label: "Receipts",
    blurb: "Attributable, governed artefact of what happened.",
  },
  {
    slug: "exception",
    label: "Exception Handling",
    blurb: "Routes conflicts, gaps and divergence to human review.",
  },
  {
    slug: "escalation",
    label: "Human Escalation",
    blurb: "Authority remains human and attributable.",
  },
];

// What KATLAS sits between — universal visual.
export const KATLAS_GOVERNS = {
  upstream: ["AI", "Models", "Automation"],
  downstream: [
    "People",
    "Organisations",
    "Platforms",
    "Payments",
    "Claims",
    "Underwriting",
  ],
};

// Underwriting workflow nodes (top-of-pathway diagram).
export const WORKFLOW = {
  pre: ["Broker", "Submission", "AI Analysis"],
  post: ["Decision", "Bind · Refer · Decline", "Claims"],
};

// Operational Control Centre tiles — synthetic, illustrative figures.
export const CONTROL_CENTRE_TILES = [
  {
    slug: "decisions",
    value: "12",
    label: "Governed decisions today",
    note: "Each carries an authority chain and a receipt.",
  },
  {
    slug: "pending-authority",
    value: "3",
    label: "Pending authority",
    note: "Awaiting authorised confirmation before downstream commits.",
    tone: "warn",
  },
  {
    slug: "conflicted-instructions",
    value: "2",
    label: "Conflicted instructions",
    note: "Routed through the exception loop for human review.",
    tone: "warn",
  },
  {
    slug: "receipts",
    value: "147",
    label: "Operational receipts",
    note: "Synthetic public-safe receipts generated in this session.",
  },
  {
    slug: "exceptions",
    value: "6",
    label: "Escalated exceptions",
    note: "Diverted from automated flow to authorised reviewers.",
  },
  {
    slug: "provenance",
    value: "98%",
    label: "Decision provenance",
    note: "Share of decisions with a complete authority chain.",
    tone: "ok",
  },
];

export const CONTROL_CENTRE_FOOTNOTE =
  "Illustrative runtime view. Figures synthetic. No live data. Not a regulated dashboard.";

// Default governance-state used on the reference scenario.
export const GOVERNANCE_STATE_DEFAULT = [
  { slug: "authority", label: "Authority", value: "Verified", tone: "ok" },
  { slug: "evidence", label: "Evidence", value: "Complete", tone: "ok" },
  { slug: "acceptance", label: "Acceptance", value: "Pending", tone: "warn" },
  { slug: "policy", label: "Policy", value: "Satisfied", tone: "ok" },
  { slug: "receipt", label: "Receipt", value: "Generated", tone: "ok" },
  { slug: "exception", label: "Exception", value: "None", tone: "muted" },
];

export const JOURNEY = [
  {
    n: "01",
    title: "Select the emerging risk",
    body:
      "Sponsor chooses a new risk, technology or market adoption theme — from AI liability to parametric triggers — as the spine of the lab.",
  },
  {
    n: "02",
    title: "Define the insurable question",
    body:
      "The lab frames what exposure, event, trigger, liability or workflow needs to be understood before the risk can be moved toward insurability.",
  },
  {
    n: "03",
    title: "Map the insurance actors",
    body:
      "Broker, client, underwriter, MGA, coverholder, reinsurer, claims handler, assessor, regulator, university partner or data provider — and the authority each holds.",
  },
  {
    n: "04",
    title: "Set the evidence boundary",
    body:
      "Define what evidence is required, who controls it, what is synthetic, what is withheld, and what may be shared with the market.",
  },
  {
    n: "05",
    title: "Bound the AI",
    body:
      "AI may suggest, triage, summarise or classify. It must not bind cover, decline cover, settle claims or make regulated decisions.",
  },
  {
    n: "06",
    title: "Produce the pathway",
    body:
      "The lab outputs a governed demonstrator, public-safe receipt, evidence map, whitepaper outline and pilot pathway — ready for sponsor and market.",
  },
];

export const THEMES = [
  { slug: "ai-adoption", title: "AI adoption and liability" },
  { slug: "cyber-referral", title: "Cyber referral governance", featured: true },
  {
    slug: "environmental",
    title: "Environmental liability and transition risk",
  },
  { slug: "digital-asset", title: "Digital asset and emerging technology risk" },
  { slug: "political-risk", title: "Political risk and supply-chain resilience" },
  { slug: "claims-triage", title: "Claims triage and authority" },
  { slug: "delegated-authority", title: "Delegated authority governance" },
  { slug: "parametric", title: "Parametric insurance triggers" },
  {
    slug: "professional-liability",
    title: "Professional liability for AI-enabled services",
  },
  {
    slug: "operational-resilience",
    title: "Operational resilience and business interruption",
  },
];

// Reference scenario: Conflicting underwriting instructions (Operational Efficiency)
export const REFERENCE_SCENARIO = {
  title: "Conflicting underwriting instructions",
  subtitle: "Operational efficiency · authority · provenance · acceptance",
  lede:
    "An underwriter grants authority by email. The platform proceeds. A second email contradicts the first. AI summarises the conflict — but it cannot resolve it. KATLAS determines authority, provenance, downstream commitments and the governed acceptance route. A receipt is produced.",
  steps: [
    {
      n: "01",
      label: "Email 1 — authority granted",
      narrative:
        "A senior underwriter writes to the MGA confirming binding authority for a class extension on a delegated facility, subject to standard wording.",
      whyMatters:
        "Operational throughput in specialty insurance is shaped by email-driven authority. The starting instruction is real, time-stamped and acted upon.",
      whyAIInsufficient:
        "A general AI assistant would treat the email as ground truth and progress the workflow without checking provenance or downstream commitments.",
      whereGovernance:
        "KATLAS captures who issued the instruction, the authority held at that moment and what downstream actions the platform is now permitted to take.",
    },
    {
      n: "02",
      label: "Platform proceeds",
      narrative:
        "The MGA's operations team begins binding actions on the facility. Coverholder records update. A broker is notified. Reinsurance attachment is queued.",
      whyMatters:
        "Each downstream commitment creates obligations to brokers, clients and reinsurers that are operationally and contractually expensive to unwind.",
      whyAIInsufficient:
        "Generative AI has no awareness of cascading commitments. It cannot tell which steps are reversible and which are not.",
      whereGovernance:
        "Runtime governance records each step, its actor, the authority relied upon and whether the action is reversible. This becomes the spine of the receipt.",
    },
    {
      n: "03",
      label: "Email 2 — contradicting instruction",
      narrative:
        "A second email arrives — from the same underwriter or a peer with equal seniority — withdrawing or qualifying the authority granted in email 1.",
      whyMatters:
        "Conflicting instructions are commonplace in specialty insurance. The cost is rarely the contradiction itself; it is the absence of a governed route to resolve it.",
      whyAIInsufficient:
        "AI can summarise the difference between the two emails but cannot decide which instruction governs, who is authorised to resolve the conflict, or what to do with work already in flight.",
      whereGovernance:
        "KATLAS treats the second instruction as a governance event: it pauses downstream actions, surfaces what has already committed and routes the resolution to an authorised human.",
    },
    {
      n: "04",
      label: "AI summarises — bounded",
      narrative:
        "The AI assistant produces a side-by-side summary: original authority, contradicting instruction, divergence, downstream commitments to date, and evidence gaps.",
      whyMatters:
        "Senior underwriters do not want a black-box answer; they want a clear, attributable view of the divergence and what has happened in the meantime.",
      whyAIInsufficient:
        "Left unbounded, AI would propose a resolution. That would be a regulated act. Here, the AI is explicitly forbidden from deciding.",
      whereGovernance:
        "The AI-use boundary is recorded on the receipt. Suggestions are labelled as suggestions. No decision is taken by the model.",
    },
    {
      n: "05",
      label: "Governed resolution",
      narrative:
        "An authorised underwriter — identified by role, not by mailbox — reviews the divergence, the downstream commitments and the evidence map, and issues a resolving instruction.",
      whyMatters:
        "Authority must remain human and attributable. Acceptance is the moment the workflow becomes auditable to the market and to regulators.",
      whyAIInsufficient:
        "Acceptance is the part AI cannot do. The lab's purpose is to make that boundary visible and operational, not to dissolve it.",
      whereGovernance:
        "KATLAS records the authorised decision, the evidence relied upon, the evidence withheld, the downstream actions reversed or confirmed, and the resolution time.",
    },
    {
      n: "06",
      label: "Receipt produced",
      narrative:
        "A public-safe receipt summarises the workflow: actors, authority, evidence boundary, AI-use boundary, authorised decision, suggested next step and verification state.",
      whyMatters:
        "The receipt is the artefact the market understands: a governed, attributable record that can be shown to a broker, a client or a regulator.",
      whyAIInsufficient:
        "AI alone produces text. KATLAS produces an attributable, governed artefact that a market can rely upon.",
      whereGovernance:
        "The receipt is clearly labelled SIMULATED in this lab. In a live deployment, KATLAS signs the receipt at runtime.",
      successLooksLike:
        "Sponsor, broker, university and underwriter can read the same receipt and reach the same conclusion about what happened, why, and who was authorised.",
    },
  ],
};

// Exception loop — used as an inline diagram on the reference scenario.
export const EXCEPTION_LOOP = [
  { n: "01", label: "AI recommendation", tone: "muted" },
  { n: "02", label: "Conflict detected", tone: "warn" },
  { n: "03", label: "Governance node", tone: "katlas" },
  { n: "04", label: "Human review", tone: "warn" },
  { n: "05", label: "Receipt", tone: "ok" },
  { n: "06", label: "Decision", tone: "ok" },
];

// Cyber referral governance sample scenario
export const CYBER_SAMPLE = {
  title: "Cyber Referral Governance Playground",
  blurb:
    "A broker submits a cyber risk that falls outside automated appetite. The client has partial evidence, the cyber assessor has technical findings, and the underwriter needs a governed referral route. The AI assistant may summarise gaps and suggest next evidence requests, but only the authorised underwriter can approve, decline, refer or request more information.",
};

export const LAB_OUTPUTS = [
  "Emerging risk brief",
  "Insurable question",
  "Actor and authority map",
  "Evidence-boundary map",
  "AI-use boundary",
  "Synthetic demonstrator",
  "Public-safe sample receipt",
  "Sponsor thought-leadership note",
  "Whitepaper outline",
  "Pilot pathway and budget band",
];

export const THOUGHT_LEADERSHIP_BULLETS = [
  "Demonstrate thought leadership in complex and emerging risks",
  "Educate brokers, clients and university partners",
  "Explore AI-enabled risk workflows without using live customer data",
  "Show how underwriting authority remains human and attributable",
  "Convert market themes into pilot pathways and whitepaper outputs",
  "Create a sponsor-branded evidence pack for internal and market discussion",
];

// Adoption Path replaces the three sponsor packages. This frames KATLAS as
// a capability evaluation, not a consultancy engagement.
export const ADOPTION_STAGES = [
  {
    slug: "stage-1",
    eyebrow: "Stage 1",
    title: "One governed handoff",
    body:
      "A single multi-party action — a broker submission, a coverholder referral, a delegated-authority instruction — placed inside the KATLAS runtime. One workflow. One receipt. One authority chain. The minimum viable proof.",
    deliverable: "Single governed workflow + receipt",
  },
  {
    slug: "stage-2",
    eyebrow: "Stage 2",
    title: "Department workflow",
    body:
      "A full operational function — referrals, exceptions, claims triage or delegated-authority oversight — moved end-to-end through the runtime. Authority, evidence, policy and exceptions all governed and attributable.",
    deliverable: "Department-level operational runtime",
  },
  {
    slug: "stage-3",
    eyebrow: "Stage 3",
    title: "Cross-organisational pilot",
    body:
      "Broker, MGA, insurer and reinsurer transacting through the same governance engine. Receipts cross organisational boundaries. The market sees one attributable record.",
    deliverable: "Multi-party governed pilot",
  },
  {
    slug: "stage-4",
    eyebrow: "Stage 4",
    title: "Operational deployment",
    body:
      "KATLAS as the governance layer of record for live operational workflows. Sectoral journeys (insurance, freight, health, cities) sit on top of the same runtime.",
    deliverable: "Production runtime governance",
  },
];

export const INSURANCE_LINES = [
  "Cyber",
  "Environmental liability",
  "Political risk",
  "Political violence",
  "Transactional liability",
  "Professional liability",
  "Financial institutions",
  "Excess casualty",
  "Other",
];

export const AUDIENCES = [
  { slug: "brokers", label: "Brokers" },
  { slug: "corporates", label: "Corporate clients" },
  { slug: "mga", label: "MGAs / coverholders" },
  { slug: "underwriters", label: "Underwriters" },
  { slug: "claims", label: "Claims teams" },
  { slug: "reinsurers", label: "Reinsurers" },
  { slug: "regulators", label: "Regulators / policy stakeholders" },
  { slug: "university", label: "University partners" },
];

export const OUTPUTS = [
  { slug: "briefing", label: "Sponsored briefing note" },
  { slug: "whitepaper", label: "Whitepaper outline" },
  { slug: "roundtable", label: "Roundtable pack" },
  { slug: "demonstrator", label: "Synthetic demonstrator" },
  { slug: "university-lab", label: "University lab project" },
  { slug: "education", label: "Market education session" },
  { slug: "pilot", label: "Pilot pathway" },
];

export const BUDGET_BANDS = [
  "Exploratory (under £25k)",
  "£25k – £75k",
  "£75k – £150k",
  "£150k+",
  "To be discussed",
];

export const FOOTER_DISCLAIMER =
  "Synthetic insurance innovation playground only. No live underwriting, claims, pricing or regulated advice. KATLAS supplies governed workflow technology and consultancy. Working title and sponsor branding subject to approval.";
